/**
 * Battle Constants
 *
 * Single source of truth for all PVP combat constants.
 * Used by both backend (game logic) and frontend (UI display).
 */

import type { CombatTuning } from '../domain/petModels.js';
import { calculateScaledHp } from '../domain/levelScaling.js';

/**
 * Core combat constants used in battle calculations and UI display.
 *
 * NOTE: Stat scaling (HP, damage, heal) is now level-based, not stage-based.
 * See shared/domain/levelScaling.ts for the new scaling system.
 */
export const COMBAT_CONSTANTS = {
  /**
   * **THE BELL — how many rounds a 1v1 battle gets before it is settled on remaining HP.**
   *
   * Reached with both fighters standing, the higher HP wins and equal HP is a draw. It governs
   * exactly the modes that run through `pvpBattleService` — Trial, Ranked and Challenge — plus
   * the Telegram bot's own engine. **The CAMPAIGN is not one of them**, even on a single-slot
   * node: campaign fights are built by `createSquadBattle` and resolved by `squadBattleService`,
   * which has no bell and runs until one side is wiped out. (`campaign-gauntlet.ts` models a node
   * as a chain of `simulateBattle` fights and therefore DOES apply this constant — a harness that
   * carries a rule the mode it measures does not have. Recorded in tuning log Row 49; it is not
   * this constant's to fix.)
   *
   * **15, not 10 — because the bell is the least interesting way a fight can end, and the
   * ramp already had a better one.** A battle that reaches the bell was not won, it was
   * measured: nobody was knocked out, and the result is whichever bar happened to be taller.
   * {@link PRESSURE_PER_ROUND} exists precisely so that a fight nobody can finish finishes
   * itself, and it had barely started doing that work by the old bell — round 10 landed at
   * 2.5x, the first round where blows are big enough to close a stalemate outright, and the
   * fight ended there. Five more rounds is where the ramp gets to pay off: round 15 landed at
   * 3.75x, and a bar that survived ten rounds does not survive five at that size.
   *
   * **The ramp has since been RESHAPED TWICE and this argument was re-checked against each**
   * (tuning log Rows 52 and 53): it starts a round earlier than it did and climbs at +13.6%
   * rather than +25%, so round 10 lands at 1.95x and round 15 at 2.63x. The reasoning above
   * survives both — 2.63x is still a size no bar that has been fought over for fifteen rounds
   * walks away from — and the measurement agrees each time: draws read 1.79% after Row 52 and
   * 1.7% after Row 53, against the 1.84% they read before either, so the bell is not catching
   * any more fights than it was. **Never read the multipliers in this paragraph as live
   * values**; {@link getPressureMultiplier} is the only place they are true.
   *
   * **MEASURED, and one of the two numbers moved the other way** (audit sweep, `--battles 20
   * --levels 5,10,20,30 --seeds 1337,42`; 10 → 15): criterion 10 reads avg turns 8.2 → 8.7,
   * burst p99 59.6% → 63.0%, and draws 0.8% → **1.8%**, which is a RISE. Draws were expected to
   * fall because fewer fights are settled on the taller bar — and almost none ever were: at the
   * old bell only **2.3%** of battles reached it at all and **0.04%** ended level on it, so
   * essentially every draw in this game is a SIMULTANEOUS KNOCKOUT, and five more rounds at
   * 2.75x-3.75x pressure is exactly where two fighters delete each other in the same exchange.
   * The band is `≤ 10%`, so 1.8% is comfortably inside it, but the mechanism is recorded here
   * because the obvious prediction was wrong.
   *
   * **What it bought is criterion 13.** The winner's lowest bar was 60.2% — pinned to the
   * ceiling of the 10-60% band and UNSTABLE across seeds — and reads 57.7% at the longer bell:
   * the winner is now actually pushed, rather than winning a fight that stopped before it got
   * dangerous. Every surface that PRINTS the bell derives it from here.
   */
  BASE_TURNS: 15,
  /**
   * **ELEMENT ADVANTAGE — half again of the caster's own magic** (Fire→Nature→Thunder→Water→Fire).
   * Read through {@link getElementAdvantageBonus}, never multiplied by hand.
   *
   * It was a flat +15, which is a different rule at every level: 79% of a Lv1 median spell and
   * 45% of a Lv20 one, so the rider was LOUDEST where levels are lowest — the campaign's first
   * world, where a Lv2 rival's spell more than doubled against a Lv10 player. A share is anchored
   * at level 20 (median spell 33 → +17, against +15 before), the healthiest row the game has
   * (`COMBAT_MAX_LEVEL`), and shrinks with the spell below it. Tuning log Row 74.
   */
  ELEMENT_ADVANTAGE_SHARE: 0.5,
  /** HP percentage (50%) below which Execute effects deal bonus damage */
  EXECUTE_THRESHOLD: 0.5,
  /**
   * HP fraction (20%) at or below which a fighter reads as "critically low — one hit
   * from death": the red pulsing HP bar, the low-hp portrait mood, and the tired
   * portrait image all turn on together here. Purely a VISUAL cue — deliberately
   * decoupled from {@link EXECUTE_THRESHOLD} (the 50% execute-damage mechanic), which
   * must never move for a UI change.
   */
  LOW_HP_VISUAL_THRESHOLD: 0.2,
  /**
   * HP fraction (40%) below which the CASTER's own Last Stand heal card pays double. Read off the
   * caster's own bar — what the player can see — and deliberately not {@link EXECUTE_THRESHOLD},
   * which is about the foe's.
   */
  LOW_HP_HEAL_THRESHOLD: 0.4,
  /**
   * **A STORED MAGIC CHARGE — a third again of the caster's own magic**, when it is consumed.
   * Read through {@link getChargedMagicBonus}. Was a flat +10, for the same reason and with the
   * same anchor as {@link COMBAT_CONSTANTS.ELEMENT_ADVANTAGE_SHARE} (median Lv20 spell → +11).
   */
  CHARGED_MAGIC_SHARE: 1 / 3,
  /** Damage multiplier for consecutive attack uses (50% reduction = 0.5x, fatigue penalty) */
  CONSECUTIVE_SKILL_DAMAGE_MULTIPLIER: 0.5,
  /** Chance for precise attack to hit through dodge (50% = 0.5) */
  PRECISE_ATTACK_HIT_CHANCE: 0.5,
  /**
   * **WHAT A SUCCESSFUL DODGE COUNTERS FOR, as a share of the attack it evaded.**
   *
   * The base it multiplies is the OPPONENT's `attack.damage`, never the dodger's — see
   * {@link dodgeCounterDamage}. At 1.0 a dodge hands back exactly the swing it stepped out of.
   */
  DODGE_COUNTER_MULTIPLIER: 1.0,
  /** Number of turns a fresh bleed ticks for (per-turn damage lands at the END of each) */
  BLEED_DURATION_TURNS: 4,
  /**
   * Fraction of the bleed's rolled value dealt at the END of EACH bleed turn.
   *
   * BLEED_DURATION_TURNS × BLEED_DAMAGE_FRACTION is what a full bleed is WORTH, as a
   * share of its rolled value, and it must stay at or just under 1: the rolled value
   * grows +1/level while HP grows +1/level, so a product above 1 is a DoT that outruns
   * the HP pool it is chipping. (At 4 turns × 0.5 a bleed gained +2 effective damage per
   * level against +1 HP, which made INFECT run away with the early meta.)
   *
   * **0.2 rather than 0.25 — a full bleed now returns 80% of its rolled value, not all
   * of it.** A bleed cost one turn and paid its whole value back for free while the
   * fighter that applied it kept swinging, which made opening on a bleed the strongest
   * first move on the board and made DETONATE the largest number in the game. With the
   * rounding in {@link calculateBleedPerTurn} the attack Bleed's printed ladder goes
   * 3/5/8/10 → 2/4/6/8, which is a nerf to the DRIP and not to the card: the exchange it
   * has to win, the {@link BLEED_DURATION_TURNS} it runs for, and the punishes that read
   * it are all untouched.
   */
  BLEED_DAMAGE_FRACTION: 0.2,
  /**
   * **THE MOST A BLEED MAY EVER BE WORTH — its whole run, as a share of the bar it is
   * chipping.** The tick ceiling is derived from it in {@link maxBleedPerTurn}.
   *
   * **Why a bleed needs a ceiling when nothing else does.** Every other number in the deck is
   * spent once: a stack of Execute lands, the shield eats what it can, and the turn is over.
   * A bleed is the one effect that keeps paying, and the attack Bleed is the one card that
   * DEEPENS — it adds its tick to a bleed already running and restarts the clock. Nothing
   * bounded that: measured over 2166 cards-on battles at L20, the drip reached **36 per turn,
   * which is 144 HP against a 94 HP bar** — one card, stacked, worth more than the fighter it
   * was killing. That is the shape {@link SHIELD_CASH_OUT_CAP} already exists to stop on the
   * other side of the deck, and this is the same cap on this side.
   *
   * **A SHARE OF THE BAR, not a flat number, because a CARD's value is flat.** A card is
   * printed at one value at every level while HP grows with the level, so a flat ceiling would
   * bind on a bleed at level 5 and never again — and worse, the same 4-copy bleed that costs a
   * third of a bar at 40 costs most of one at 5. Reading the ceiling off the target's own max
   * HP is what makes a flat card proportional to the fighter it is used on, at every level.
   *
   * **0.4.** Four in ten is a bar a player can hold — *a bleed can take at most 40% of you* —
   * and it is above every ordinary bleed the game deals (the median tick is 5 of a possible 9,
   * and 78% of the ticks measured were already under the ceiling), so what it removes is the
   * tail and nothing else: the stacked drip, and the element-matched full commit that took 60%
   * of a bar on top of a round it had already won. It is deliberately NOT a nerf to
   * {@link BLEED_DAMAGE_FRACTION} or {@link BLEED_DURATION_TURNS} — those two are one
   * invariant that must keep multiplying to about 1 (see the note above), and moving either
   * would nerf the DoT species' own signature, which is the roster's WEAKEST archetype
   * (41% cards-on) and not what is too strong here.
   */
  BLEED_MAX_SHARE_OF_MAX_HP: 0.4,
  /**
   * Number of turns a fresh wound lasts. A wound now runs on its own clock —
   * attacking no longer breaks it — so the debuff survives long enough for a
   * partner to exploit it (re-applying refreshes the duration).
   */
  WOUND_DURATION_TURNS: 4,
  /** Fraction of a heal that actually lands while the healer is wounded (each heal is halved while wounded) */
  WOUNDED_HEAL_MULTIPLIER: 0.5,
  /**
   * Bleed ticks a DETONATE punish (`exploit_dot` on magic) can cash in at once.
   *
   * Capped BELOW {@link COMBAT_CONSTANTS.BLEED_DURATION_TURNS} on purpose: detonating a
   * fresh full-duration bleed would turn a 4-turn drip into one hit for everything it
   * had left to give,
   * and a stacked Battle Card bleed makes that the largest single number the game can
   * produce. Three ticks keeps detonation a tempo choice rather than a burst combo.
   */
  DETONATE_MAX_TICKS: 3,
  /**
   * Ceiling on a CASH OUT punish (`exploit_shield` on magic), so converting a shield into
   * damage cannot scale without limit off stacked Battle Card shields.
   *
   * Sits above every species' own shield (9-14) and above a two-copy card shield (16), and
   * below a three-copy one (24) — a Wall species can always spend what it raised itself,
   * and can spend most of a card, but never all of a maximal stack.
   */
  SHIELD_CASH_OUT_CAP: 20,
  /**
   * Damage a plain attack deals when a species carries no attack rider.
   *
   * The four Wall species always ran this; it became the roster-wide baseline when the
   * `exploit_*` riders moved off attack and onto the magic signature as `punish`.
   */
  PLAIN_ATTACK_DAMAGE: 14,
  /**
   * **CRITICAL HIT: how often a turn lands for half again — CURRENTLY 0, THE MECHANIC IS OFF.**
   *
   * **This is the whole switch.** `rollCriticalHit` in the battle engine returns `false` at a
   * rate of 0 without drawing off the seeded stream, so at 0 no fight rolls, no log line is
   * written, no `challengerCrit` / `defenderCrit` ever comes back true, and every surface that
   * DRAWS a crit — the arena callout, the round panel's enlarged figure and shake, the battle
   * log — goes quiet because it is told about a crit rather than deciding there was one.
   * {@link CRITICAL_HITS_ENABLED} is derived from this number so the pages that EXPLAIN the
   * mechanic go quiet with it; nothing else needs to know.
   *
   * **Off for now, at the owner's call, not because a number wanted moving.** Turning it back
   * on is this one field: put 30% back and every surface below returns with it. The argument
   * the rate was chosen on is kept here rather than deleted, because it is what a restore has
   * to re-read:
   *
   * > Rolled ONCE PER FIGHTER PER TURN, on all four gestures — it is a property of the TURN,
   * > not of a card, a suit or a skill. A crit multiplies everything that fighter's move moved
   * > this turn by {@link CRITICAL_HIT_MULTIPLIER}: the skill's damage, the meld's damage, and
   * > the healing, together.
   * >
   * > **One roll, not four.** A per-card or per-suit crit would be four independent rolls a
   * > player cannot hold in their head, and it would price differently on every card in the
   * > deck — the binary magic cards have no magnitude to multiply at all. One roll on the turn's
   * > own total is the same rule for every gesture, which is what lets the arena say it in
   * > three words and the round panel mark it with one badge.
   * >
   * > 30% is deliberately loud. It is high enough that a player sees one in most battles
   * > (5-6 turns × two fighters) and low enough that it is not the plan — you still win on the
   * > wheel and the cards, and a crit is the round that got away from someone.
   *
   * **The `COMBAT_CRIT_CHANCE` test override still works**, and is the only thing that can put
   * a crit in a turn while this reads 0 — which is what keeps the crit suite asserting the
   * multiplier it will be restored to rather than being deleted alongside the rate.
   */
  CRITICAL_HIT_CHANCE: 0,
  /**
   * What a critical hit multiplies by — damage and healing alike.
   *
   * **1.5, not 2.** Doubling read as the obvious number — "critical" is a word players arrive
   * already knowing — but it priced the roll wrong at {@link CRITICAL_HIT_CHANCE}: at 30% a
   * crit is not a rare accident, it is most rounds of most battles, and a mechanic that fires
   * that often and doubles is the mechanic the fight is decided by. Half again is still the
   * loudest thing that can happen to a move and still swings an exchange, without making the
   * roll worth more than the two decisions — the triangle and the commit — the round is
   * actually played on.
   *
   * Half again is also a figure a player reads off the HP bar without being taught it, which
   * is the only bar a non-double multiplier has to clear. Every surface that PRINTS the figure
   * derives it from here, through `CRITICAL_HIT_MULTIPLIER_LABEL` in `combatSkillPresentation`
   * — nothing in the game writes "double" by hand.
   */
  CRITICAL_HIT_MULTIPLIER: 1.5,


  /**
   * **ROUND PRESSURE — how many rounds a fight gets at its printed numbers before every blow
   * starts landing harder.**
   *
   * After this many rounds, both fighters' damage grows by {@link COMBAT_CONSTANTS.PRESSURE_PER_ROUND}
   * every round, for the rest of the fight. Symmetric, unavoidable, and applied to the blows a
   * move lands — never to a bleed tick, which is a previous turn's business.
   *
   * **Four, because that is where a fight stops being decided and starts being administered.**
   * Fights averaged **8.6 rounds** with the winner's bar never falling below **68%** of its own
   * maximum: the first four rounds decided it and the remaining four collected on it. A player
   * does not experience that as a long fight, they experience it as a won one that will not end.
   *
   * **Now three, because four free rounds is what made the ramp a CLIFF.** The pair of dials is
   * tuned together and only makes sense read together: the free rounds set where the climb
   * starts, {@link COMBAT_CONSTANTS.PRESSURE_PER_ROUND} sets how steep it is, and the thing a
   * player actually feels is neither — it is the RATIO between a blow early and the same blow
   * at round 8, 9, 10. At 4 free rounds and +25% that ratio was 2.0x / 2.25x / 2.5x: the same
   * Attack button, pressed with the same card, for two and a half times the number it was worth
   * when the fight opened. Not a fight that got dangerous — two different fights, and the first
   * one did not count. Moving the climb one round earlier and cutting its step from +25% to
   * +17% keeps the total damage a fight delivers by round 8 within half a percent of what it
   * was — 10.50x against 10.55x — while cutting those three ratios to 1.85x / 2.02x / 2.19x.
   * The fight is the same length; it is no longer two different fights. Tuning log Row 52.
   *
   * **The free rounds did NOT move again when the step came down to +13.6%** (tuning log Row 53).
   * That step was cut on its own, so the climb still starts in the same place and only its
   * gradient changed; the three ratios above now read 1.68x / 1.82x / 1.95x.
   */
  PRESSURE_FREE_ROUNDS: 1,

  /**
   * **What each round past {@link COMBAT_CONSTANTS.PRESSURE_FREE_ROUNDS} adds to every blow.**
   *
   * Round 5 lands at 1.12x, round 8 at 1.30x, and round 10 at 1.42x — where it now STOPS, for
   * the rest of the fight however long it runs. See {@link COMBAT_CONSTANTS.PRESSURE_MAX_ROUND}
   * for why the ceiling this dial spent its whole life refusing turned out to be safe: healing
   * is finite, so a plateau still ends a fight nobody can finish.
   *
   * **+6%, BECAUSE THIS DIAL WAS NEVER THE ONLY RAMP AND WAS ALWAYS TUNED AS IF IT WERE.**
   * Every argument below reads pressure ALONE — "round 10 reads 1.63x", "round 15 still lands at
   * 2.08x" — and a player has never once met pressure alone. Battle evolution is the second ramp
   * and it is reached on a CLOCK rather than as a choice: measured over 6,000 simulated battles,
   * the attacker's average evolution multiplier climbs from 1.04x at round 1 to the full top rung
   * by round 9, and rank 3 is near-universal from round 8. What lands on the player is the
   * PRODUCT, and at +9% with the old +40% evolution damage that product was **2.28x at round 10
   * and 2.91x at the bell** — numbers nobody chose, because nothing in the score could see them
   * ({@link getPressureMultiplier}'s own criterion, 21, divided by the evolved bar and cancelled
   * evolution out of its own measurement).
   *
   * **The bell test is what picks +6%, and it is this dial's own test read on the product.**
   * The argument below for +9% is that round 15 must still land around 2.08x, or "a bar fought
   * over for fifteen rounds walks away from a blow". With {@link EVOLUTION_RANK_DAMAGE_BONUS}
   * restored to half the HP dial, +6% puts the bell at **2.06x as a product** — the same figure
   * that argument was written on, now measuring the thing the player meets instead of one of its
   * two factors. Round 10 lands at **1.70x** as a product against 2.28x before, and round 8 at
   * 1.56x. Production told us why this mattered: paired within a battle, real 5.9 fights were
   * running a median **3.22x** late-over-early, with the p90 at 5.91x.
   *
   * **Historically: +9%, because ROUND 10 had never actually cleared the argument the dial was
   * reshaped on.**
   * The owner's call (tuning log Row 56), and the instrument agrees with them. At +13.6% round 10
   * landed at **1.95x** — a hair under the 2.0x that {@link COMBAT_CONSTANTS.PRESSURE_FREE_ROUNDS}'s
   * own note names as the failure ("not a fight that got dangerous — two different fights, and the
   * first one did not count"). Two successive cuts walked that number down to just short of the
   * line and never over it. At +9% round 10 reads **1.63x**: a blow lands three-fifths harder than
   * it did at the open, which is dangerous rather than a different game.
   *
   * **The BELL is what picked 0.09 rather than 0.08.** Round 15 still lands at 2.08x, which keeps
   * {@link COMBAT_CONSTANTS.BASE_TURNS}'s own argument intact — a bar fought over for fifteen
   * rounds does not walk away from a blow that size. At 0.08 the bell falls to 1.96x and that
   * reasoning stops holding; at 0.10 round 10 reads 1.72x, which is barely a move. 0.09 is the
   * largest cut the bell can carry.
   *
   * **Historically: +13.6%, because the ramp was still outrunning the heal.** A fifth of the step
   * came off (0.17 → 0.136) in the same change that raised every species' Recover by half
   * (`createRecoverSkill` in `speciesConfig.ts`), and the two belong together: pressure grows
   * every blow for the rest of the fight while a heal is a flat number, so the LATE-fight
   * question is not "how big is the heal" but "how big is the heal against the blow it has to
   * outlast". Raising the heal alone would have been eaten by round 9; cutting the step alone
   * would have made a long fight safe. See the paired measurement in tuning log Row 53 — the two
   * dials were moved together and CANNOT be attributed separately from that run.
   *
   * **Historically: +17%, not +25% — and the free rounds came down to 3 in that step, because these two
   * are one dial.** See {@link COMBAT_CONSTANTS.PRESSURE_FREE_ROUNDS} for the argument. **The
   * pair was chosen to hold the fight's LENGTH while changing its SHAPE**, and the arithmetic is
   * what picked them: cumulative damage through round 8 is 10.50x under the old dial and 10.55x
   * under this one — a fight delivers the same total, distributed differently. Round 4 lands
   * 17% harder than it used to and round 5 7% harder; round 8 lands 7% softer, round 10 12%
   * softer and round 15 19% softer, the two curves crossing between rounds 6 and 7.
   *
   * **What it measured**, `--battles 20 --levels 5,10,20,30 --seeds 1337,42`, against the same
   * sweep on the old dial: **avg turns 8.68 → 8.68** — the length held, which is the whole
   * design of the pair — burst p99 63.0% → **60.2%** (band ≤70%), the winner's lowest bar 57.7%
   * → 57.8% (band 10-60%), a typical hit 20.3% → 20.4% of a bar, draws 1.84% → 1.79%, dead
   * rounds 0.67% → 0.66%. The verdict of every criterion held, each read at the sample it names.
   * The number this change is FOR is criterion 21, which did not exist until it: a late blow
   * over an early one, **1.81x → 1.70x** (and **1.65x** once the step came down to +13.6%).
   *
   * **A cost worth naming:** criterion 17 (the move you pick matters) reads +5.8pts → +5.2pts
   * against a ≥+10pts floor. It was already failing, and it drifts here for a real reason — a
   * steeper ramp multiplies the gesture's damage, so flattening it shrinks what the gesture
   * choice swings. That is inherent to compressing the spread and is not this dial's to repair:
   * 17's own knob is the damage a GESTURE carries against the damage a MELD adds.
   *
   * **The ramp is now on the CONTROLS, not only in the log.** `GestureTray` prices every button
   * through `getPressureMultiplier` at the round about to be played, exactly as `resolveTurn`
   * charges it — so the four numbers under a player's thumb climb every round from round
   * {@link COMBAT_CONSTANTS.PRESSURE_FREE_ROUNDS} + 1 on. Before that the only place this
   * mechanic was ever stated mid-fight was a battle-log line that scrolls past under a panel
   * nobody reads on the round it matters, and the buttons quietly under-quoted themselves by
   * the whole ramp.
   *
   * **This is the dial that decoupled two criteria that had been fighting each other.** 13 (the
   * winner was pushed) wanted a bigger HP pool so a fight ran long enough to be dangerous; 15 (a
   * hit is worth a bar you can count) wanted a smaller one so a hit was a share you could see.
   * On the HP dial those are the same lever pulled in opposite directions, which is exactly why
   * neither ever passed. Pressure gives them different levers: the fight keeps its early rounds
   * cheap AND its late rounds land like an ending.
   *
   * **HALVED, +6% → +3% a round, on the owner's call** ("damage increases too much each turn").
   * The step is the dial a player feels as *the fight keeps getting more dangerous*. At +3% the
   * ramp tops out at **1.21x** at round 10 (it was 1.42x), and 1.21x on the top evolution rung
   * is 1.57x as a product (it was 1.85x) — still under {@link COMBAT_CONSTANTS.MAX_DAMAGE_MODIFIER}.
   * The measured before/after is in the tuning log.
   *
   * The ceiling to watch is criterion 10's burst p99 — one exchange must never be able to delete
   * a bar — and the floor is its average-turns check, since this shortens every fight it touches.
   */
  PRESSURE_PER_ROUND: 0.03,

  /**
   * **THE ROUND THE RAMP STOPS CLIMBING — the ceiling this mechanic spent its whole life not
   * having.**
   *
   * Past this round every blow is worth exactly what it was worth here:
   * {@link getPressureMultiplier} of {@link COMBAT_CONSTANTS.PRESSURE_MAX_ROUND}, for the rest
   * of the fight however long it runs. With the dials at 3 free rounds and +6%, that tops the
   * ramp out at **1.42x**, and against a fighter on the top evolution rung
   * ({@link EVOLUTION_RANK_DAMAGE_BONUS}) the most any round of this game can multiply a printed
   * number by is **1.42 x 1.2 = 1.70x**.
   *
   * **It exists because of the CAMPAIGN, which is the one mode with no bell.** A 1v1 is settled
   * at {@link COMBAT_CONSTANTS.BASE_TURNS} whatever the bars say, so its ramp was always bounded
   * in practice even while being unbounded in principle. A campaign fight is a SQUAD fight and
   * runs until one side is wiped out — three bodies a side, no count — so rounds 16, 20, 25 are
   * ordinary there, and an uncapped ramp meant a campaign round could multiply a blow by
   * arbitrarily much. Nothing measured it: the Game Health bands were all argued on two fighters
   * and a bell, and 3v3 is scored for health nowhere.
   *
   * **The note this replaces said a cap was the one thing this dial must never have** — "a
   * ceiling is a plateau, and a plateau is where a stalemate would sit down again". That
   * reasoning has a premise, and the premise is false: it assumes healing can keep pace with a
   * flat multiplier forever. **It cannot, because healing is FINITE.** A fighter gets
   * `CARDS_MAX_RECOVER_USES` recovers per battle and no more; once both sides have spent them,
   * every further round is damage against a bar nothing can refill, and the fight ends whether
   * the ramp is still climbing or not. The ramp's job is to stop a fight being *waited out*, and
   * a 1.42x plateau does that job exactly as well as a number that keeps growing — what the
   * growth added past round 10 was not resolution, it was only size.
   *
   * **Round 10, because that is where the player's own reading of the fight has already been
   * honoured.** Criterion 21 contrasts rounds 1-3 with round 8 and later, and round 10 is the
   * round named in the complaint this whole line of work answers. A ramp that keeps climbing
   * past the last round anybody measured is a ramp making promises no instrument is checking.
   */
  PRESSURE_MAX_ROUND: 10,

  /**
   * **THE HARD CEILING ON EVERYTHING A ROUND CAN DO TO A PRINTED NUMBER — 2x, and it is
   * ENFORCED rather than arrived at.**
   *
   * Two multipliers reach a move's damage and no others do: the round it is thrown in
   * ({@link getPressureMultiplier}) and the rung the thrower stands on
   * (`EVOLUTION_RANK_DAMAGE_BONUS`). This bounds their PRODUCT, which is the only quantity a
   * player ever meets, through {@link getEffectivePressureMultiplier}.
   *
   * **Why a clamp and not just dials that happen to fit.** At the dials this shipped with the
   * product reaches **2.002x** at round {@link COMBAT_CONSTANTS.PRESSURE_MAX_ROUND} on the top
   * rung — over the line by a tenth of a percent, which is invisible in play and still a broken
   * promise. Picking a step of 0.0598 instead would hide the problem rather than solve it: the
   * next person to move ANY of the four dials would walk back over the line with no warning,
   * exactly as happened when `EVOLUTION_RANK_DAMAGE_BONUS` drifted to the size of the HP dial
   * and nothing noticed for a release. A ceiling that is a rule cannot drift; a ceiling that is
   * an arithmetic coincidence is only ever one edit from being false.
   *
   * **2x is the line the game's own vocabulary already draws.** Criterion 21's ceiling is set at
   * 2.0 on the argument that doubling is where what a player learned in the opening rounds stops
   * being merely stale and becomes wrong by more than the thing it describes — "one more hit
   * will do it" false by a whole hit. The same number belongs on the mechanic, not only on the
   * measurement of it.
   */
  MAX_DAMAGE_MODIFIER: 2,
} as const;

/**
 * HP actually restored by a heal while the healer is wounded.
 * A wound halves EVERY heal while it lasts; rounds up so a positive heal never drops to 0.
 * Single source of truth for both the battle engine and the UI preview.
 */
export function calculateWoundedHeal(heal: number): number {
  return Math.ceil(heal * COMBAT_CONSTANTS.WOUNDED_HEAL_MULTIPLIER);
}

/**
 * **WHAT A SUCCESSFUL DODGE COUNTERS FOR — and it is THEIR attack, not the dodger's.**
 *
 * A dodge throws no punch of its own. It steps aside and lets the swing carry its owner past,
 * so the figure it lands is the attack it evaded: the number on the OPPONENT's Attack button,
 * at {@link COMBAT_CONSTANTS.DODGE_COUNTER_MULTIPLIER}. That is the read a player can act on —
 * *the harder they hit, the worse it is to swing at a dodge* — where sizing the counter on the
 * dodger's own attack made a defensive move scale with the one stat the dodger was not using.
 *
 * Pure and shared, because two surfaces have to agree on it to the point: the engine deals it
 * (`resolveTurn`) and the Dodge button previews it before the round is committed. They were
 * two `Math.ceil` expressions in two files, which is exactly how a button starts advertising
 * damage the server does not deal.
 *
 * @param opponentAttackDamage the LEVEL-SCALED `attack.damage` of the fighter being dodged
 */
export function dodgeCounterDamage(opponentAttackDamage: number): number {
  return Math.ceil(opponentAttackDamage * COMBAT_CONSTANTS.DODGE_COUNTER_MULTIPLIER);
}

/**
 * **A CRITICAL HIT, APPLIED — the one place the multiplier is spent.**
 *
 * Pure and shared, and it lives here beside the rate it spends so that any surface which ever
 * has to PREVIEW a crit reads the same function the fight rolls with — the mistake
 * `applyMatchedElement` was written to avoid, where a card face multiplied separately from the
 * engine. Rounds up, for the same reason {@link calculateWoundedHeal} does: a number the player
 * was told grew must never come back smaller than the multiplier promised — and with a
 * fractional multiplier the rounding is what stops a 1 from critting for 1.
 *
 * Total on `crit === false`, which is what makes it safe to call unconditionally at every
 * site rather than guarding each one — a non-crit turn returns exactly the value it was given.
 */
export function applyCriticalHit(value: number, crit: boolean): number {
  if (!crit || value <= 0) return value;
  return Math.ceil(value * COMBAT_CONSTANTS.CRITICAL_HIT_MULTIPLIER);
}

/**
 * **A SHARE OF A SPELL, AS DAMAGE.** Rounded, and never less than 1 on a spell that does damage —
 * a rider the player was told about must always land as something.
 */
function shareOfSpell(magicDamage: number, share: number): number {
  if (!Number.isFinite(magicDamage) || magicDamage <= 0) return 0;
  return Math.max(1, Math.round(magicDamage * share));
}

/**
 * **WHAT ELEMENT ADVANTAGE ADDS TO THIS SPELL** — the one place it is computed, for the engine,
 * the swap-in hit, the AI and every button that prices a move. `magicDamage` is the caster's own
 * magic skill damage as the fight will use it (level- and mode-scaled).
 */
export function getElementAdvantageBonus(magicDamage: number): number {
  return shareOfSpell(magicDamage, COMBAT_CONSTANTS.ELEMENT_ADVANTAGE_SHARE);
}

/** Element advantage as the whole-number percent the pages print ("+50%"). Derived, never typed. */
export const ELEMENT_ADVANTAGE_PERCENT = Math.round(COMBAT_CONSTANTS.ELEMENT_ADVANTAGE_SHARE * 100);

/** A consumed magic charge as the whole-number percent the pages print ("+33%"). */
export const CHARGED_MAGIC_PERCENT = Math.round(COMBAT_CONSTANTS.CHARGED_MAGIC_SHARE * 100);

/** **WHAT A CONSUMED MAGIC CHARGE ADDS TO THIS SPELL** — see {@link getElementAdvantageBonus}. */
export function getChargedMagicBonus(magicDamage: number): number {
  return shareOfSpell(magicDamage, COMBAT_CONSTANTS.CHARGED_MAGIC_SHARE);
}

/**
 * **IS THE CRIT A RULE OF THIS GAME RIGHT NOW?** Derived, never typed.
 *
 * The engine needs no such flag — it rolls at {@link COMBAT_CONSTANTS.CRITICAL_HIT_CHANCE} and a
 * rate of 0 is already silence. This exists for the surfaces that EXPLAIN the mechanic in prose
 * — the Battle Cards page, the Help page, the llms.txt rules dump — because a rule the engine
 * cannot apply is a rule those pages must not teach. A player who reads "30% of moves land
 * CRITICAL" and never sees one in fifty battles has been told the game works a way it does not,
 * which is worse than not mentioning it.
 *
 * Read off the rate rather than set beside it deliberately: two switches would let the pages and
 * the engine disagree, and the one that would have been forgotten is this one.
 */
export const CRITICAL_HITS_ENABLED: boolean = COMBAT_CONSTANTS.CRITICAL_HIT_CHANCE > 0;


/**
 * **THE PRESSURE MULTIPLIER FOR A GIVEN ROUND — the one place the ramp is computed.**
 *
 * `round` is 1-based: the first exchange of a battle is round 1. Exactly 1 for the first
 * {@link COMBAT_CONSTANTS.PRESSURE_FREE_ROUNDS} rounds, so an ordinary short fight resolves at
 * the numbers on the card and this mechanic is invisible to it.
 *
 * Defensive about its input for the same reason `getEvolutionRankForCards` is: the round is read
 * off a deserialised `battle_state`, and a corrupt value must degrade to "no pressure" rather
 * than to `NaN` damage in a live fight.
 */
export function getPressureMultiplier(round: number): number {
  if (!Number.isFinite(round)) return 1;
  // Clamped BEFORE the arithmetic, so round 11 and round 40 are the same round to this mechanic.
  // See PRESSURE_MAX_ROUND: the campaign has no bell, and an unbounded ramp there had no bound.
  const counted = Math.min(Math.floor(round), COMBAT_CONSTANTS.PRESSURE_MAX_ROUND);
  const past = counted - COMBAT_CONSTANTS.PRESSURE_FREE_ROUNDS;
  if (past <= 0) return 1;
  return 1 + past * COMBAT_CONSTANTS.PRESSURE_PER_ROUND;
}

/**
 * **WHAT THE ROUND ACTUALLY CHARGES THIS FIGHTER — the ramp, held under the 2x ceiling.**
 *
 * `getPressureMultiplier` is the ramp on its own; this is the ramp as it is applied to a fighter
 * standing on a given rung, and it is the figure the engine charges AND the figure the gesture
 * buttons quote. The two must be the same call or a button advertises damage the server will not
 * deal — the rule `PvpGestureTray` exists to keep.
 *
 * `evolutionDamageMultiplier` is passed as a NUMBER rather than a rank so this module stays free
 * of a dependency on `battleEvolution` (which would be a cycle: the evolution module reads combat
 * constants). Callers hand in `getEvolutionDamageMultiplier(rank)`.
 *
 * Defensive about its input for the same reason the ramp itself is: these figures are re-derived
 * from a deserialised `battle_state`, and a corrupt one must degrade to the unclamped ramp rather
 * than to `NaN` damage in a live fight.
 */
export function getEffectivePressureMultiplier(
  round: number,
  evolutionDamageMultiplier: number
): number {
  const pressure = getPressureMultiplier(round);
  if (!Number.isFinite(evolutionDamageMultiplier) || evolutionDamageMultiplier <= 0) {
    return pressure;
  }
  // The ceiling is on the PRODUCT, so what the ramp may still add is whatever the rung has not
  // already spent of it.
  const headroom = COMBAT_CONSTANTS.MAX_DAMAGE_MODIFIER / evolutionDamageMultiplier;
  return Math.min(pressure, headroom);
}

/**
 * **THE WHOLE MODIFIER ON A PRINTED NUMBER — ramp times rung, under the ceiling.**
 *
 * The number the Help page's table prints, and the one thing a player actually experiences. Never
 * larger than {@link COMBAT_CONSTANTS.MAX_DAMAGE_MODIFIER}, by construction rather than by
 * arithmetic that happens to land there.
 */
export function getDamageModifier(round: number, evolutionDamageMultiplier: number): number {
  return getEffectivePressureMultiplier(round, evolutionDamageMultiplier) * evolutionDamageMultiplier;
}

/**
 * Damage a bleed lands at the END OF EACH of its {@link COMBAT_CONSTANTS.BLEED_DURATION_TURNS}
 * turns, for a given rolled value. Rounds up, and never drops below 1 — a bleed that ticks
 * for nothing is not a bleed.
 *
 * Single source of truth for the engine (`applyBleed`) and for anything that has to PRINT a
 * bleed. A Battle Card's face shows this number rather than the rolled value it comes from:
 * the rolled value is an intermediate that appears nowhere in the fight, so printing it left
 * the player holding a number they could not find on the HP bar or in the log.
 *
 * **ROUNDS TO NEAREST, not up.** Rounding up settled every fraction in the bleeder's favour,
 * four times per bleed, on top of a tick that was already paying the rolled value back in
 * full — and it collapsed the collection ladder: with a Common at 0.85 of its Rare, `ceil` put
 * a full stack of both on the same printed tick, so a shelf would have sold a rung that bought
 * nothing (`cardTierUpgrade.test.ts` is what catches that). Nearest keeps the rungs apart and
 * is the other half of the nerf. The floor of 1 stays: a bleed that ticks for nothing is not
 * a bleed.
 */
export function calculateBleedPerTurn(bleedValue: number): number {
  return Math.max(1, Math.round(bleedValue * COMBAT_CONSTANTS.BLEED_DAMAGE_FRACTION));
}

/**
 * **THE CEILING ON A BLEED'S TICK, for the bar it is being applied to.**
 *
 * {@link COMBAT_CONSTANTS.BLEED_MAX_SHARE_OF_MAX_HP} says what a whole bleed may be worth;
 * this divides it back into the per-turn figure the state actually carries, because
 * `dotDamage` is a tick and every surface that reads it reads a tick.
 *
 * Rounds to nearest and floors at 1, exactly as {@link calculateBleedPerTurn} does — the
 * ceiling must never be able to sit below the floor, or a bleed on a very small bar would
 * be clamped to nothing and stop being a bleed.
 *
 * Shared rather than written into `applyBleed`, because the ceiling is a rule the game
 * TEACHES: the help page states it, and it is derived there from this function rather than
 * retyped as a number.
 *
 * @param maxHp the bleeding fighter's max HP — the bar the share is taken of
 */
export function maxBleedPerTurn(maxHp: number): number {
  return Math.max(
    1,
    Math.round(
      (maxHp * COMBAT_CONSTANTS.BLEED_MAX_SHARE_OF_MAX_HP) / COMBAT_CONSTANTS.BLEED_DURATION_TURNS
    )
  );
}

/**
 * **WHAT A WHOLE BLEED IS WORTH**, as a percentage of the value it was rolled at — the two
 * bleed constants multiplied, which is the only form a player ever needs them in.
 *
 * Three help surfaces state this sentence and all three used to end it "…so a full bleed deals
 * about its whole value", which was true only while the duration and the fraction happened to
 * multiply to 1. They no longer do, on purpose ({@link COMBAT_CONSTANTS.BLEED_DAMAGE_FRACTION}),
 * so the figure is derived here rather than asserted in prose three times.
 */
export const BLEED_FULL_VALUE_PERCENT = Math.round(
  COMBAT_CONSTANTS.BLEED_DURATION_TURNS * COMBAT_CONSTANTS.BLEED_DAMAGE_FRACTION * 100
);

/**
 * **THE BLEED CEILING AS A PLAYER READS IT** — the one sentence every help surface states:
 * *a bleed can never take more than this much of your bar.*
 *
 * Derived from {@link COMBAT_CONSTANTS.BLEED_MAX_SHARE_OF_MAX_HP} rather than typed beside it,
 * for the same reason {@link BLEED_FULL_VALUE_PERCENT} is: the ceiling is quoted on the help
 * page, the PVP help page, the Battle Cards page and the static content served to crawlers,
 * and four hand-typed percentages are four places for the number to outlive the rule.
 */
export const BLEED_MAX_SHARE_PERCENT = Math.round(
  COMBAT_CONSTANTS.BLEED_MAX_SHARE_OF_MAX_HP * 100
);

/**
 * Action-specific consecutive use limits.
 * After reaching the limit, the action is locked and player is forced to use a different action.
 */
export const ACTION_LOCK_LIMITS = {
  attack: 2,   // Attack can be used 2 times consecutively, 3rd is blocked
  magic: 2,    // Magic can be used 2 times consecutively, 3rd is blocked
  recover: 4,  // Recover can be used 4 times (rarely reached due to total use limits)
  dodge: 2,    // Dodge can be used 2 times consecutively, 3rd is blocked (see combatActionRules)
} as const;

/**
 * Default combat tuning values.
 * Applied when species config doesn't specify custom tuning.
 */
export const DEFAULT_COMBAT_TUNING: Required<CombatTuning> = {
  actionLockStreak: 4,  // Legacy default, now overridden by ACTION_LOCK_LIMITS
  forceActionChance: 0.95,
  blockActionChance: 0.95,
};

/**
 * Element advantage wheel:
 * Fire -> Nature -> Thunder -> Water -> Fire
 */
export const ELEMENT_ADVANTAGE: Record<string, string> = {
  Fire: 'Nature',
  Nature: 'Thunder',
  Thunder: 'Water',
  Water: 'Fire',
} as const;

/**
 * Calculate maximum HP using level-based scaling.
 *
 * Formula: baseHp + (level - 1)
 *
 * Evolution stages no longer affect HP - all species scale equally with level.
 * `baseHp` from speciesConfig IS the level-1 pool; there is no global multiplier
 * between the config and the board. A pool wide enough that single hits can't
 * one-shot is each species' own baseHp, asserted in levelScaling.test.ts.
 *
 * @param baseHp - Base HP from species combat config
 * @param level - Current level (1-60+)
 * @returns Maximum HP for this combatant
 *
 * @example
 * calculateMaxHp(52, 20) // 87 HP (round(52×1.3)=68 + 19)
 * calculateMaxHp(65, 20) // 104 HP (round(65×1.3)=85 + 19)
 */
export function calculateMaxHp(baseHp: number, level: number): number {
  return calculateScaledHp(baseHp, level);
}
